/**
 * SelectorTurnosFijos.jsx
 *
 * Sub-fase 6.B · Componente compartido para selección de turnos fijos.
 *
 * ⚠️ NO se integra todavía en ningún modal · sólo está exportado para tests.
 * La integración real entra en:
 *   - 6.C · ModalInscripcionClasesFijas.jsx
 *   - 6.D · pestaña Inscripción de ClientNewProfile.jsx
 *   - 6.E · ClientModal.jsx (futura, si procede)
 *
 * Recupera y unifica la lógica buena que vive ya en ClientModal.jsx y la
 * pestaña Inscripción de ClientNewProfile.jsx:
 *   - render array.slice(0, maxHours) · N filas exactas, sin "+ Añadir/Quitar"
 *   - reset hora al cambiar día
 *   - aforo por slot (disabled visual via isSlotFull/getSlotInfo)
 *   - intensidad por slot (color via getHorarioIntensidad)
 *
 * Añade además (lo que ninguna de las dos tenía):
 *   - cross-row disabled: día/hora ya seleccionados en otra fila quedan disabled
 *   - aviso visual de membresía mal configurada (horas_semana=null)
 *   - regla "no repetir día" configurable (permitir/warning/blocking)
 *   - validación de duplicados (par exacto) con feedback visual
 *
 * NO hace llamadas HTTP, NO toca BD. Recibe TODOS los helpers como props.
 */

import React from "react";
import {
  ajustarTurnosAHorasSemana,
  detectarFilasPerdidasAlAjustar,
  validarNoDuplicarDiaHora,
  validarNoRepetirDia,
  REGLA_DIAS,
} from "../../utils/turnosFijos";

const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

/**
 * Props:
 *   turnos                  · array [{dia, hora}] · controlado por padre
 *   onChange(turnosNuevos)  · callback con el array modificado
 *   horasSemana             · int|null. Si null, muestra aviso bloqueante
 *   horariosDisponibles     · dict {dia: [hora, ...]} · si falta, fallback a <input type="time">
 *   isSlotFull(dia, hora)   · fn opcional · marca disabled si aforo lleno
 *   getSlotInfo(dia, hora)  · fn opcional · devuelve "ocupados/aforo" como label
 *   getHorarioIntensidad    · fn opcional · devuelve {nombre, color}
 *   reglaDiasDistintos      · 'permitir' | 'warning' | 'blocking' · default 'warning'
 *   disabled                · bool · deshabilita todos los selectores
 *   label                   · string opcional · etiqueta del bloque
 *   onValidacion(estado)    · callback opcional · informa al padre del
 *                              estado validación (ok, errores, warnings)
 *   onTurnosRecortados      · Sub-fase 6.C ajuste Ann: callback que se invoca
 *                              cuando bajar `horasSemana` provoca recorte
 *                              de filas que tenían día+hora rellenados.
 *                              Permite al padre mostrar toast.warning con
 *                              detalle de qué se perdió.
 *                              Firma: (filasPerdidas) => void
 *                              donde filasPerdidas = [{indice, dia, hora}].
 *   CustomColorSelect       · componente opcional para hora · si falta usa <select>
 */
const DIAS_LABEL = "Día...";
const HORA_LABEL = "Hora...";

function SelectorTurnosFijos({
  turnos,
  onChange,
  horasSemana,
  horariosDisponibles,
  isSlotFull,
  getSlotInfo,
  getHorarioIntensidad,
  reglaDiasDistintos = REGLA_DIAS.WARNING,
  disabled = false,
  label = "Horario (turnos fijos)",
  onValidacion,
  onTurnosRecortados,
  CustomColorSelect,
  // PR-BECA (Ann 2026-07-06): en modo becado se muestran hasta `horasSemana`
  // filas pero el alta es válida con 1..horasSemana turnos COMPLETOS (las filas
  // vacías se ignoran). Sólo bloquea filas parcialmente rellenas, duplicados o
  // día repetido en modo blocking. En modo normal (becado=false) el
  // comportamiento es idéntico al anterior (exactamente horasSemana completos).
  becado = false,
}) {
  // ── Aviso bloqueante: cuota mal configurada ─────────────────────────
  const horasValida = (
    typeof horasSemana === "number"
    && Number.isInteger(horasSemana)
    && horasSemana >= 1
  );

  // ── Auto-ajustar el array a horasSemana cuando cambie ───────────────
  // Patrón useEffect controlado por longitud · evita recursión.
  // Sub-fase 6.C ajuste Ann: si bajamos horasSemana y los turnos sobrantes
  // tienen contenido relleno, notificar al padre ANTES del recorte vía
  // onTurnosRecortados para que pueda mostrar toast.warning.
  React.useEffect(() => {
    if (!horasValida) return;
    const longitudObjetivo = horasSemana;
    const fuente = Array.isArray(turnos) ? turnos : [];
    if (fuente.length === longitudObjetivo) return;
    // Detectar pérdida de filas con contenido (sólo si se va a recortar)
    if (fuente.length > longitudObjetivo
        && typeof onTurnosRecortados === "function") {
      const { hayPerdida, filasPerdidas } = detectarFilasPerdidasAlAjustar(
        fuente, longitudObjetivo);
      if (hayPerdida) {
        onTurnosRecortados(filasPerdidas);
      }
    }
    const ajustado = ajustarTurnosAHorasSemana(fuente, longitudObjetivo);
    if (typeof onChange === "function") onChange(ajustado);
  }, [horasSemana, horasValida]);

  // ── useEffect de notificación · DEBE estar ANTES de cualquier return ─
  // condicional para mantener el orden de hooks constante (Rules of Hooks).
  // Toda llamada a `onValidacion` vive dentro del useEffect: NUNCA durante
  // el render (eso causaría setState-during-render → bucle infinito).
  const turnosSerializados = JSON.stringify(
    Array.isArray(turnos) ? turnos : []);
  React.useEffect(() => {
    if (typeof onValidacion !== "function") return;
    if (!horasValida) {
      onValidacion({
        ok: false,
        code: "CUOTA_HORAS_SEMANA_NO_CONFIGURADA",
        razon: "La membresía seleccionada no tiene horas semanales configuradas.",
      });
      return;
    }
    // Cálculo local · evita depender de `filas` que se construye en el render
    const filasLocal = ajustarTurnosAHorasSemana(
      Array.isArray(turnos) ? turnos : [], horasSemana);
    const dupLocal = validarNoDuplicarDiaHora(filasLocal);
    const diasRepLocal = validarNoRepetirDia(filasLocal, reglaDiasDistintos);
    if (becado) {
      // Modo becado · válido con 1..horasSemana filas COMPLETAS. Las filas
      // totalmente vacías se ignoran; las parciales (sólo día o sólo hora) sí
      // invalidan (para no mandar un turno a medias al backend).
      const completas = filasLocal.filter((t) => t.dia && t.hora);
      const parciales = filasLocal
        .map((t, i) => ((t.dia && !t.hora) || (!t.dia && t.hora) ? i + 1 : null))
        .filter((v) => v !== null);
      onValidacion({
        ok: completas.length >= 1
            && completas.length <= horasSemana
            && parciales.length === 0
            && dupLocal.ok
            && !diasRepLocal.bloquea,
        becado: true,
        turnos_completos: completas.length,
        incompletos: parciales,
        duplicados: dupLocal.repetidos,
        dias_repetidos: diasRepLocal.dias_repetidos,
        regla_dias: reglaDiasDistintos,
        bloquea_dias: diasRepLocal.bloquea,
      });
      return;
    }
    const incompletos = filasLocal
      .map((t, i) => (!t.dia || !t.hora ? i + 1 : null))
      .filter((v) => v !== null);
    onValidacion({
      ok: incompletos.length === 0 && dupLocal.ok && !diasRepLocal.bloquea,
      incompletos,
      duplicados: dupLocal.repetidos,
      dias_repetidos: diasRepLocal.dias_repetidos,
      regla_dias: reglaDiasDistintos,
      bloquea_dias: diasRepLocal.bloquea,
    });
  }, [horasValida, horasSemana, turnosSerializados, reglaDiasDistintos, becado]);

  // ── Caso: sin horas_semana válida (return temprano · todos los hooks ya
  // se han llamado arriba, no se altera el orden entre renders) ──────
  if (!horasValida) {
    return (
      <div data-testid="selector-turnos-aviso-cuota-mal"
           style={{
             padding: "10px 12px",
             border: "1px solid #fde68a",
             backgroundColor: "#fffbeb",
             borderRadius: "6px",
             color: "#92400e",
             fontSize: "12px",
           }}>
        ⚠️ La membresía seleccionada no tiene horas semanales configuradas.
        No se pueden asignar turnos hasta que se configure en el catálogo.
      </div>
    );
  }

  // ── Render normal: N filas exactas ──────────────────────────────────
  const filas = ajustarTurnosAHorasSemana(turnos, horasSemana);

  // Slots ya seleccionados (cross-row disabled)
  const paresSeleccionados = new Set(
    filas
      .map((t) => (t.dia && t.hora ? `${t.dia}#${t.hora}` : null))
      .filter(Boolean),
  );
  const diasSeleccionados = new Set(
    filas.map((t) => (t.dia ? t.dia : null)).filter(Boolean),
  );

  // Detección visual de errores en tiempo real (sólo para feedback visual ·
  // la notificación al padre vive en el useEffect de arriba).
  const dup = validarNoDuplicarDiaHora(filas);
  const diasRep = validarNoRepetirDia(filas, reglaDiasDistintos);

  function actualizarFila(idx, campo, valor) {
    const nuevo = filas.map((t, i) => {
      if (i !== idx) return { ...t };
      const f = { ...t, [campo]: valor };
      // Reset hora al cambiar día (lógica buena de ClientModal/ClientNewProfile)
      if (campo === "dia") f.hora = "";
      return f;
    });
    if (typeof onChange === "function") onChange(nuevo);
  }

  return (
    <div data-testid="selector-turnos-fijos"
         style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {label && (
        <label style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>
          {label} ({horasSemana} {horasSemana === 1 ? "horario" : "horarios"})
        </label>
      )}
      {filas.map((turno, idx) => {
        const esDuplicadoFila = dup.repetidos.some(
          (r) => r.indices && r.indices.includes(idx) && r.indices[0] !== idx,
        );
        const opcionesHora = (() => {
          if (!turno.dia) return [];
          if (!horariosDisponibles || !horariosDisponibles[turno.dia]) return [];
          return horariosDisponibles[turno.dia].map((hora) => {
            const isFull = typeof isSlotFull === "function"
              ? isSlotFull(turno.dia, hora) : false;
            const yaSeleccionadoOtraFila = paresSeleccionados.has(
              `${turno.dia}#${hora}`) && hora !== turno.hora;
            const slotInfo = typeof getSlotInfo === "function"
              ? getSlotInfo(turno.dia, hora) : "";
            const intInfo = typeof getHorarioIntensidad === "function"
              ? getHorarioIntensidad(turno.dia, hora) : null;
            const iName = intInfo?.nombre;
            const intensidadLabel = (iName && iName !== "Estándar"
                                      && iName !== "Estandar"
                                      && iName !== "undefined")
              ? ` [${iName}]` : "";
            return {
              value: hora,
              label: `${hora}${intensidadLabel} ${slotInfo ? `(${slotInfo})` : ""}`.trim(),
              color: intInfo?.color || "#9ca3af",
              disabled: isFull || yaSeleccionadoOtraFila,
            };
          });
        })();

        return (
          <div key={idx}
               data-testid={`selector-turnos-fila-${idx}`}
               style={{
                 display: "grid",
                 gridTemplateColumns: "120px 1fr",
                 gap: "8px",
                 padding: "6px 8px",
                 border: `1px solid ${esDuplicadoFila ? "#fecaca" : "#e5e7eb"}`,
                 backgroundColor: esDuplicadoFila ? "#fef2f2" : "#fff",
                 borderRadius: "6px",
                 alignItems: "center",
               }}>
            <span style={{
              fontSize: "11px", fontWeight: 600,
              color: "var(--cn-primary, #0f766e)",
            }}>
              Clase {idx + 1}
            </span>
            <div style={{ display: "grid",
                          gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
              <select
                data-testid={`selector-turno-dia-${idx}`}
                value={turno.dia}
                disabled={disabled}
                onChange={(e) => actualizarFila(idx, "dia", e.target.value)}
                style={{
                  padding: "4px 8px", fontSize: "12px",
                  border: "1px solid #d1d5db", borderRadius: "4px",
                }}
              >
                <option value="">{DIAS_LABEL}</option>
                {DIAS.map((d) => {
                  const yaUsado = diasSeleccionados.has(d) && d !== turno.dia;
                  const bloqueaDia = (
                    yaUsado && reglaDiasDistintos === REGLA_DIAS.BLOCKING);
                  return (
                    <option key={d} value={d} disabled={bloqueaDia}>
                      {d}
                      {yaUsado && reglaDiasDistintos === REGLA_DIAS.WARNING
                        ? " (otro turno)" : ""}
                    </option>
                  );
                })}
              </select>
              {/* Hora · usa CustomColorSelect si llega como prop · sino fallback */}
              {CustomColorSelect && opcionesHora.length > 0 ? (
                <CustomColorSelect
                  value={turno.hora}
                  onChange={(v) => actualizarFila(idx, "hora", v)}
                  disabled={disabled || !turno.dia}
                  placeholder={HORA_LABEL}
                  options={opcionesHora}
                  className="text-sm"
                />
              ) : (
                <select
                  data-testid={`selector-turno-hora-${idx}`}
                  value={turno.hora}
                  disabled={disabled || !turno.dia}
                  onChange={(e) => actualizarFila(idx, "hora", e.target.value)}
                  style={{
                    padding: "4px 8px", fontSize: "12px",
                    border: "1px solid #d1d5db", borderRadius: "4px",
                  }}
                >
                  <option value="">{HORA_LABEL}</option>
                  {opcionesHora.length > 0 ? (
                    opcionesHora.map((o) => (
                      <option key={o.value} value={o.value} disabled={o.disabled}>
                        {o.label}
                      </option>
                    ))
                  ) : (
                    turno.hora && <option value={turno.hora}>{turno.hora}</option>
                  )}
                </select>
              )}
            </div>
          </div>
        );
      })}

      {/* Avisos visuales bajo la lista · NO bloquean por sí mismos · el padre
          decide qué hacer con onValidacion */}
      {dup.repetidos.length > 0 && (
        <div data-testid="selector-turnos-aviso-duplicado"
             style={{
               padding: "6px 10px", backgroundColor: "#fef2f2",
               border: "1px solid #fecaca", borderRadius: "6px",
               color: "#991b1b", fontSize: "11px",
             }}>
          ❌ Horario duplicado:{" "}
          {dup.repetidos.map((r) => `${r.dia} ${r.hora}`).join(", ")}
        </div>
      )}
      {diasRep.dias_repetidos.length > 0
        && reglaDiasDistintos !== REGLA_DIAS.PERMITIR && (
        <div data-testid="selector-turnos-aviso-dia-repetido"
             style={{
               padding: "6px 10px",
               backgroundColor: reglaDiasDistintos === REGLA_DIAS.BLOCKING
                 ? "#fef2f2" : "#fffbeb",
               border: `1px solid ${reglaDiasDistintos === REGLA_DIAS.BLOCKING
                 ? "#fecaca" : "#fde68a"}`,
               borderRadius: "6px",
               color: reglaDiasDistintos === REGLA_DIAS.BLOCKING
                 ? "#991b1b" : "#92400e",
               fontSize: "11px",
             }}>
          {reglaDiasDistintos === REGLA_DIAS.BLOCKING ? "🛑" : "⚠️"} Día(s)
          repetido(s):{" "}
          {diasRep.dias_repetidos.map((r) => `${r.dia} (x${r.veces})`).join(", ")}
          {reglaDiasDistintos === REGLA_DIAS.WARNING
            && " · revisa si es intencionado"}
        </div>
      )}
    </div>
  );
}

export default SelectorTurnosFijos;
export { SelectorTurnosFijos };
